import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getConferenceTimestamp } from '../../base/conference/functions';
import { getLocalizedDurationFormatter } from '../../base/i18n/dateUtil';
import { isLocalParticipantModerator } from '../../base/participants/functions';
import { showNotification } from '../../notifications/actions';
import { ConferenceTimerDisplay } from './index';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE } from '../../notifications/constants';
import { endConference } from '../../base/conference/actions.any';
import { setUserNotified } from '../../base/conference/actions.any';
const ConferenceTimer = ({ textStyle }) => {
    const startTimestamp = useSelector(getConferenceTimestamp);
    const [timerValue, setTimerValue] = useState(getLocalizedDurationFormatter(0));
    const interval = useRef();
    const [moderatorNotified, setNotified] = useState();
    /**
     * Sets the current state values that will be used to render the timer.
     *
     * @param {number} refValueUTC - The initial UTC timestamp value.
     * @param {number} currentValueUTC - The current UTC timestamp value.
     *
     * @returns {void}
     */
    const setStateFromUTC = useCallback((refValueUTC, currentValueUTC) => {
        if (!refValueUTC || !currentValueUTC) {
            return;
        }
        if (currentValueUTC < refValueUTC) {
            return;
        }
        const timerMsValue = currentValueUTC - refValueUTC;
        const localizedTime = getLocalizedDurationFormatter(timerMsValue);
        const { dispatch, getState } = APP.store;
        if (isLocalParticipantModerator(getState())) {
            const jwt = getState()['features/base/jwt'];
            const meetingEnd = jwt.meeting?.scheduled_end || 0;
            const remainingTime = meetingEnd - currentValueUTC;
            const isProPlan = jwt.meeting?.is_pro_plan || false;
            const isFreePlan = !isProPlan;
            const { userNotified } = getState()['features/base/conference'];
            // KRAD-1822: For free plans, show warning message 5 mins before 
            // scheduled end time & end meeting once end time reached/exceeded.
            if (isFreePlan && remainingTime < 1) {
                dispatch(endConference());
            }
            else if (isFreePlan && remainingTime < 300001) {
                if (!userNotified) {
                    dispatch(setUserNotified(true));
                    var message = 'The meeting will be closed in 5 minutes.';
                    dispatch(showNotification({
                        appearance: NOTIFICATION_TYPE.WARNING,
                        titleKey: 'notify.somebody',
                        title: 'Warning',
                        descriptionKey: message
                    }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                }
            }
        }
        setTimerValue(localizedTime);
    }, []);
    /**
     * Start conference timer.
     *
     * @returns {void}
     */
    const startTimer = useCallback(() => {
        if (!interval.current && startTimestamp) {
            setStateFromUTC(startTimestamp, new Date().getTime());
            interval.current = window.setInterval(() => {
                setStateFromUTC(startTimestamp, new Date().getTime());
            }, 1000);
        }
    }, [startTimestamp, interval]);
    /**
     * Stop conference timer.
     *
     * @returns {void}
     */
    const stopTimer = useCallback(() => {
        if (interval.current) {
            clearInterval(interval.current);
            interval.current = undefined;
        }
        setTimerValue(getLocalizedDurationFormatter(0));
    }, [interval]);
    useEffect(() => {
        startTimer();
        return () => stopTimer();
    }, [startTimestamp]);
    if (!startTimestamp) {
        return null;
    }
    return (React.createElement(ConferenceTimerDisplay, { textStyle: textStyle, timerValue: timerValue }));
};
export default ConferenceTimer;
